import { atom } from 'jotai'
import { Comment, SalesStageDefinition } from '../services/api'

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'not_qualified' | 'won' | 'lost'

export interface Lead {
  id: string
  name: string
  email: string
  phone?: string
  source: string
  salaryTarget?: string
  status: LeadStatus
  assignedTo?: string
  notes?: string
  qualificationNotes?: string
  createdAt: string
  updatedAt: string
  statusHistory?: Array<{
    status: string
    timestamp: string
    changedBy?: string
  }>
  lastProposal?: string
  lastProposalGeneratedAt?: string
  lastProposalGeneratedBy?: string
  proposalSentAt?: string
  proposalSentBy?: string
  proposalSentToEmail?: string
  selectedPackageId?: string
  comments?: Comment[]
}

export const LEAD_STATUSES: LeadStatus[] = [
  'new',
  'contacted',
  'qualified',
  'not_qualified',
  'won',
  'lost',
]

export const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  not_qualified: 'Not Qualified',
  won: 'Won',
  lost: 'Lost',
}

export const STATUS_COLORS: Record<LeadStatus, string> = {
  new: '#9e9e9e',
  contacted: '#2196f3',
  qualified: '#4caf50',
  not_qualified: '#f44336',
  won: '#9c27b0',
  lost: '#795548',
}

// Atoms for dynamic stage definitions
export const salesStageDefinitionsAtom = atom<SalesStageDefinition[]>([])
export const salesStagesLoadingAtom = atom(false)

// Atoms - start with empty array, will be loaded from API
export const leadsAtom = atom<Lead[]>([])
export const leadsLoadingAtom = atom(true)
export const selectedLeadAtom = atom<Lead | null>(null)
export const leadDetailOpenAtom = atom(false)

// Derived atoms
export const leadsByStatusAtom = atom((get) => {
  const leads = get(leadsAtom) ?? []
  const salesStages = get(salesStageDefinitionsAtom) ?? []

  // Build byStatus object dynamically from active stages
  const byStatus: Record<string, Lead[]> = {}
  salesStages
    .filter((stage) => stage.isActive)
    .forEach((stage) => {
      byStatus[stage.stageId] = []
    })

  // Group leads by status
  leads.forEach((lead) => {
    if (lead?.status && byStatus[lead.status] !== undefined) {
      byStatus[lead.status].push(lead)
    }
  })

  return byStatus as Record<LeadStatus, Lead[]>
})
